import { FunctionComponent, useEffect, useState } from "react";

import { faCircleInfo, faPenToSquare } from "@fortawesome/pro-regular-svg-icons";

import { ListEventType } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { Toggle } from "@/components/Toggle/Toggle";
import { ToggleColor } from "@/components/Toggle/types";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { useUpdateListEventMutation } from "@/pages/Private/redux/list/list.api";

import { classNames } from "@/utils/classNames";

import { EventOutreachSchemaType } from "../../schema/list";
import { EventDetailsModal, EventFormPayload } from "./EventDetailsModal";
import { formatEventDate } from "./formatEventDate";

interface EventOutreachStripProps {
	listId: number;
	event?: EventOutreachSchemaType | null;
	/** Increment to open the details modal from outside (e.g. the create-campaign branch). */
	openSignal?: number;
	/** Fired after the details are saved successfully (carries the saved payload). */
	onSaved?: (payload: EventFormPayload) => void;
	/** Fired when the details modal is closed without saving (cancel/backdrop). */
	onModalClosed?: () => void;
}

/**
 * List-level "Event outreach" control (PREC-3520). Turning it on opens the details modal; the
 * saved fields become shared {event_*} variables resolved at send time across every campaign.
 */
export const EventOutreachStrip: FunctionComponent<EventOutreachStripProps> = ({
	listId,
	event,
	openSignal,
	onSaved,
	onModalClosed,
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [updateListEvent, { isLoading }] = useUpdateListEventMutation();

	const isOn = !!event?.enabled;

	// Open the modal when the parent bumps the signal (create-campaign "Event" branch).
	useEffect(() => {
		if (openSignal && openSignal > 0) {
			setIsModalOpen(true);
		}
	}, [openSignal]);

	const handleToggle = async () => {
		if (!isOn) {
			setIsModalOpen(true);

			return;
		}

		// Turn off but keep the configured data — resend it with enabled: false.
		if (!event) {
			return;
		}

		try {
			await updateListEvent({
				id: listId,
				enabled: false,
				eventName: event.eventName ?? "",
				listType: event.listType as ListEventType,
				startDate: event.startDate ?? "",
				endDate: event.endDate ?? "",
				meetingLocation: event.meetingLocation ?? undefined,
				meetingLink: event.meetingLink ?? undefined,
				meetingTimes: event.meetingTimes ?? undefined,
				customVariables: event.customVariables ?? undefined,
			}).unwrap();
		} catch {
			// Errors surfaced by the global RTK Query error middleware.
		}
	};

	const handleSave = async (payload: EventFormPayload) => {
		try {
			await updateListEvent({ id: listId, enabled: true, ...payload }).unwrap();
			setIsModalOpen(false);
			onSaved?.(payload);
		} catch {
			// Errors surfaced by the global RTK Query error middleware.
		}
	};

	const handleModalClose = () => {
		setIsModalOpen(false);
		onModalClosed?.();
	};

	const summary = isOn
		? `${event?.eventName || "this event"} · ${formatEventDate(event?.startDate)} – ${formatEventDate(
				event?.endDate
			)} · campaigns stop at end date`
		: "Share event details across campaigns";

	return (
		<>
			{/*
			 * The visible bar only becomes available once the user has picked "Event outreach" in the
			 * create-campaign branch at least once (i.e. a list_event row exists). Until then the
			 * component stays mounted — so `openSignal` can still open the details modal for that
			 * first pick — but renders no bar. (PREC-3520)
			 */}
			{event ? (
				<div
					className={classNames(
						"flex items-center gap-3 rounded-xl border px-4 py-3 mb-5",
						isOn ? "border-action-400 bg-light-blue" : "border-border-light bg-white"
					)}
				>
					<Toggle color={ToggleColor.ONCE} isChecked={isOn} onChange={handleToggle} />
					<div className="min-w-0 flex-1">
						<div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
							Event outreach
							<Tooltip content="Set event details once — name, dates, meeting info — and drop them into any campaign as {event_name}, {meeting_link} and more. Editing them updates every campaign on this list.">
								<Icon className="text-gray-400" icon={faCircleInfo} />
							</Tooltip>
						</div>
						<div
							className={classNames("truncate text-xs", isOn ? "text-gray-700" : "text-gray-500")}
						>
							{summary}
						</div>
					</div>
					{isOn ? (
						<button
							className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold text-brand-light hover:bg-white/60"
							type="button"
							onClick={() => setIsModalOpen(true)}
						>
							<Icon icon={faPenToSquare} /> Edit details
						</button>
					) : null}
				</div>
			) : null}

			<EventDetailsModal
				event={event}
				isLoading={isLoading}
				isOpened={isModalOpen}
				title={event?.enabled ? "Edit event details" : "Set up event details"}
				onClose={handleModalClose}
				onSave={handleSave}
			/>
		</>
	);
};
